<template>
	<base-dialog :close-on-backdrop="closeOnBackdrop" :close-on-esc="closeOnEsc" @close="emit('close')">
		<div :class="['dialog-wrap', customClass, { 'dialog-wrap--closable': closable }]">
			<button
				v-if="closable"
				type="button"
				class="dialog-close dialog-close--inside"
				aria-label="Закрыть"
				@click="emit('close')"
			>
				<svg-icon name="cross" />
			</button>
			<slot>
				<p class="dialog-title">{{ title }}</p>
				<div class="dialog-btn-group">
					<base-button :text="firstBtnText" variant="secondary" @click="handleClickFirstBtn" :class="'btn-one'" />
					<base-button text="Нет" @click="handleClickSecondBtn" v-if="showSecondBtn" />
				</div>
			</slot>
		</div>
	</base-dialog>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import BaseDialog from '@/components/base/BaseDialog.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import SvgIcon from '@/components/SvgIcon.vue'

const emit = defineEmits<{
	close: []
	clickFirstBtn: []
	clickSecondBtn: []
}>()

function handleClickFirstBtn() {
	emit('clickFirstBtn')
}

function handleClickSecondBtn() {
	emit('clickSecondBtn')
}

const props = defineProps({
	title: { type: String },
	customClass: { type: String, default: '' },
	firstBtnText: { type: String, default: 'Да' },
	showSecondBtn: { type: Boolean, default: true },
	// Шторке про деньги закрытие кликом мимо запрещено: случайный промах в момент,
	// когда платёж обрабатывается, читается как потеря платежа.
	closeOnBackdrop: { type: Boolean, default: true },
	closeOnEsc: { type: Boolean, default: true },
	// Трёхзначный: undefined — решает наличие слота, true/false — явное указание.
	// default обязателен, иначе Vue приводит отсутствующий Boolean к false.
	showClose: { type: Boolean, default: undefined }
})

const slots = useSlots()

// Крестик по умолчанию есть там, где диалог рисует «шапку» сам (диалоги подтверждения).
// Диалог со своим содержимым получает его только явным show-close.
const closable = computed(() => props.showClose ?? !slots.default)
</script>

<style lang="scss" scoped>
.dialog {
	&-wrap {
		border-top-left-radius: var(--radius-sheet);
		border-top-right-radius: var(--radius-sheet);
		padding: 16px;
		padding-bottom: 48px;
		max-width: 500px;
		width: 100%;
		background-color: var(--background-primary);
		@include flex-column-center;
		@media (min-width: $screen-mobile) {
			position: absolute;
			margin: auto;
			border-radius: var(--radius-lg);
			max-width: 420px;
		}
		&::before {
			width: 40px;
			height: 4px;
			border-radius: var(--radius-full);
			opacity: 0.24;
			background-color: var(--sheet-handle);
			content: '';
			display: block;
			margin-bottom: 24px;
			@media (min-width: $screen-mobile) {
				display: none;
			}
		}
		// Крестик внутри панели перекрывает её верх, поэтому содержимое сдвигается ниже него.
		&--closable {
			@media (min-width: $screen-mobile) {
				padding-top: 48px;
			}
		}
	}
	&-close {
		position: absolute;
		display: none;
		padding: 0;
		border: none;
		background: none;
		cursor: pointer;
		@media (min-width: $screen-mobile) {
			display: flex;
		}
		&--inside {
			top: 12px;
			right: 12px;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 32px;
			border-radius: 50%;
			background-color: var(--background-secondary);
			color: var(--text-secondary);
			transition: $transition-bg, $transition-color;
			// Глиф cross заполняет весь viewBox 24×24, а проп size у SvgIcon не работает
			// (число в :style браузер игнорирует) — размер задаётся только через CSS.
			.svg-icon {
				width: 12px;
				height: 12px;
			}
			&:hover {
				background-color: var(--background-secondary-hover);
				color: var(--text-primary);
			}
			&:active {
				color: var(--text-primary);
			}
		}
	}
	&-cookies-wrap {
		padding-bottom: 16px;
		&::before {
			margin-bottom: 12px;
		}
	}
	&-title {
		font: var(--font-heading);
		color: var(--text-primary);
		margin-bottom: 24px;
		text-align: center;
	}
	&-description {
		font: var(--font-body-small);
		color: var(--text-inverse);
		margin-bottom: 16px;
	}
	&-btn-group {
		display: flex;
		& > *:first-child {
			margin-right: 8px;
		}
		& .btn {
			width: 80px;
			height: 40px;
			border-radius: var(--radius-md);
			&-one {
				width: 100%;
			}
		}
	}
}
.cancel-flow-wrap {
	padding-bottom: 16px;
}
// Шторка исчерпанного лимита: нижние 48px из .dialog-wrap не нужны,
// отступ вокруг содержимого одинаковый со всех сторон.
.limit-wrap {
	padding-bottom: 16px;
}
// Шторка несостоявшегося продления — по тому же правилу, что и соседняя.
.suspended-wrap {
	padding-bottom: 16px;
}
@if $is-mts {
	.dialog-logout {
		.btn-secondary {
			width: auto;
			height: auto;
			padding: 15px 32px;
			border-radius: var(--radius-md);
			background: var(--background-secondary);
			color: var(--text-primary);
			text-align: center;
		}
		.btn:not(.btn-secondary) {
			width: auto;
			height: auto;
			padding: 15px 32px;
			border-radius: var(--radius-md);
			background: var(--background-inverse);
			color: var(--text-inverse);
		}
	}
}
</style>
